import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, fmtTime12, fmtDateLong } from "@/lib/api";
import { useContent, imgUrl } from "@/context/ContentContext";
import { ArrowRight, Frame, Scissors, Sparkles, Heart, Home as HomeIcon, PaintBucket } from "lucide-react";

const STEP_ICONS = [PaintBucket, Frame, Scissors, Sparkles, HomeIcon];
const COLOR_BG = { ochre: "bg-ochre text-white", forest: "bg-forest text-white", coral: "bg-coral text-white", terracotta: "bg-terracotta text-white", charcoal: "bg-charcoal text-cream" };

export default function Home() {
  const [featured, setFeatured] = useState([]);
  const [testimonials, setTestimonials] = useState([]);
  const { home, site } = useContent();

  useEffect(() => {
    api.get("/workshops").then((r) => setFeatured(r.data.filter((w) => w.featured).slice(0, 3)));
    api.get("/testimonials").then((r) => setTestimonials(r.data));
  }, []);

  const sections = {
    hero: <Hero c={home.hero} site={site}/>,
    services: <Services c={home.services}/>,
    how_it_works: <HowItWorks c={home.how_it_works}/>,
    featured: <Featured c={home.featured} items={featured}/>,
    corporate: <Corporate c={home.corporate}/>,
    testimonials: <Testimonials c={home.testimonials} items={testimonials}/>,
  };

  return <div>{home.sections.filter((s) => !s.hidden).map((s) => <div key={s.id} data-testid={`home-section-${s.id}`}>{sections[s.id]}</div>)}</div>;
}

function Hero({ c, site }) {
  return (
    <section className="relative overflow-hidden">
      <div className="splat splat-pink w-72 h-72 -top-20 -right-20 opacity-40"></div>
      <div className="splat splat-cyan w-56 h-56 top-40 -left-20 opacity-30"></div>
      <div className="splat splat-purple w-40 h-40 bottom-16 right-1/3 opacity-25"></div>
      <div className="splat splat-pink w-6 h-6 top-32 right-1/4"></div>
      <div className="splat splat-cyan w-5 h-5 top-56 right-1/2"></div>
      <div className="max-w-7xl mx-auto px-6 lg:px-8 pt-14 pb-24 grid lg:grid-cols-2 gap-14 items-center relative z-10">
        <div className="animate-fade-up">
          {c.badge && <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-charcoal text-cream text-xs font-bold tracking-widest uppercase">
            <span className="w-2 h-2 rounded-full bg-terracotta animate-pulse"></span> {c.badge}
          </div>}
          <h1 className="mt-6 font-display text-5xl sm:text-6xl md:text-[5.5rem] leading-[0.95] text-charcoal">
            {c.heading_line1} <span className="text-terracotta">{c.heading_accent1}</span><br/>
            {c.heading_line2} <span className="text-forest">{c.heading_accent2}</span>
          </h1>
          <p className="mt-8 text-lg md:text-xl text-charcoal/75 max-w-lg font-medium">{c.description}</p>
          <div className="mt-10 flex flex-wrap gap-4">
            {c.button1_label && <Link to={c.button1_link} data-testid="hero-book-workshop-btn" className="inline-flex items-center gap-2 rounded-full bg-terracotta text-white px-8 py-4 font-bold shadow-button transition-transform hover:-translate-y-1 hover:bg-terracotta-light">{c.button1_label} <ArrowRight size={18}/></Link>}
            {c.button2_label && <Link to={c.button2_link} data-testid="hero-plan-event-btn" className="inline-flex items-center gap-2 rounded-full bg-charcoal text-cream border-2 border-charcoal px-8 py-4 font-bold transition-transform hover:-translate-y-1 hover:bg-charcoal/90">{c.button2_label}</Link>}
          </div>
        </div>
        <div className="relative">
          <div className="absolute -top-10 -right-4 w-48 h-48 rounded-full bg-ochre/50 blur-3xl"></div>
          <div className="absolute -bottom-6 -left-6 w-40 h-40 rounded-full bg-forest/40 blur-3xl"></div>
          {c.image && <img src={imgUrl(c.image)} alt="" className="relative rounded-[2rem] shadow-hover w-full h-[520px] object-cover border-4 border-white"/>}
          {c.badge_value && <div className="absolute -bottom-6 -left-6 bg-white rounded-2xl p-4 shadow-hover flex items-center gap-3 border-2 border-charcoal">
            <div className="w-10 h-10 rounded-full bg-terracotta grid place-items-center text-white"><Heart size={18} strokeWidth={2.5}/></div>
            <div><div className="text-xs text-charcoal/60 font-semibold uppercase tracking-wider">{c.badge_label}</div><div className="font-serif text-charcoal text-lg leading-none">{c.badge_value}</div></div>
          </div>}
          {site.logo_image && <div className="hidden md:block absolute -top-6 -right-6 bg-white rounded-2xl p-2 shadow-hover border-2 border-charcoal rotate-6 animate-wiggle">
            <img src={imgUrl(site.logo_image)} alt={site.business_name} className="h-20 w-auto"/>
          </div>}
        </div>
      </div>
    </section>
  );
}

function Services({ c }) {
  return (
    <section className="max-w-7xl mx-auto px-6 lg:px-8 py-24 relative">
      <div className="splat splat-purple w-32 h-32 top-16 -left-16 opacity-25"></div>
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-12 relative z-10">
        <div>
          <div className="text-terracotta text-sm tracking-widest uppercase font-bold">{c.eyebrow}</div>
          <h2 className="mt-2 font-serif text-4xl md:text-6xl text-charcoal max-w-xl leading-none">{c.heading}<br/><span className="text-ochre">{c.heading_accent}</span></h2>
        </div>
        <p className="text-charcoal/75 max-w-md font-medium">{c.description}</p>
      </div>
      <div className="grid md:grid-cols-4 gap-6 auto-rows-[220px]">
        {c.items.map((s, i) => (
          <Link key={i} to={s.link || "/"} data-testid={`service-card-${i}`}
            className={`${s.image ? "bg-white" : COLOR_BG[s.color] || "bg-white"} ${s.large ? "md:col-span-2 md:row-span-2" : ""} rounded-3xl p-8 shadow-card hover:shadow-hover transition-transform hover:-translate-y-1 relative overflow-hidden flex flex-col justify-end group border-2 border-transparent hover:border-charcoal/10`}>
            {s.image && <img src={imgUrl(s.image)} alt={s.title} className="absolute inset-0 w-full h-full object-cover opacity-80 group-hover:scale-105 transition-transform"/>}
            {s.image && <div className="absolute inset-0 bg-gradient-to-t from-charcoal/80 via-charcoal/20 to-transparent"></div>}
            <div className={`relative ${s.image ? "text-white" : ""}`}>
              <h3 className="font-serif text-2xl md:text-3xl leading-none">{s.title}</h3>
              <p className={`mt-2 text-sm font-medium ${s.image ? "text-white/90" : "opacity-85"}`}>{s.description}</p>
              <div className="mt-3 inline-flex items-center gap-1.5 text-sm font-bold">Learn more <ArrowRight size={14}/></div>
            </div>
          </Link>
        ))}
      </div>
    </section>
  );
}

function HowItWorks({ c }) {
  return (
    <section className="bg-charcoal text-cream py-24 relative overflow-hidden">
      <div className="splat splat-pink w-40 h-40 top-8 right-8 opacity-25"></div>
      <div className="splat splat-cyan w-32 h-32 bottom-10 left-8 opacity-25"></div>
      <div className="max-w-7xl mx-auto px-6 lg:px-8 relative z-10">
        <div className="text-center max-w-2xl mx-auto">
          <div className="text-terracotta-light text-sm tracking-widest uppercase font-bold">{c.eyebrow}</div>
          <h2 className="mt-2 font-serif text-4xl md:text-6xl leading-none text-cream">{c.heading} <span className="text-forest-light">{c.heading_accent}</span></h2>
          <p className="mt-5 text-cream/75 font-medium">{c.description}</p>
        </div>
        <div className="mt-16 grid md:grid-cols-5 gap-6">
          {c.steps.map((s, idx) => {
            const Icon = STEP_ICONS[idx % STEP_ICONS.length]; const n = String(idx + 1).padStart(2, "0");
            return (
              <div key={idx} className="bg-white/5 rounded-3xl p-6 border border-white/10 backdrop-blur-sm hover:bg-white/10 transition-colors" data-testid={`step-${n}`}>
                <div className={`step-number text-4xl ${idx % 2 === 0 ? "text-terracotta-light" : "text-forest-light"}`}>{n}</div>
                <Icon size={22} className="mt-3 text-ochre-light" strokeWidth={1.5}/>
                <h3 className="mt-3 font-serif text-lg leading-tight">{s.title}</h3>
                <p className="mt-2 text-sm text-cream/70">{s.description}</p>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}

function Featured({ c, items }) {
  return (
    <section className="max-w-7xl mx-auto px-6 lg:px-8 py-24 relative">
      <div className="splat splat-cyan w-32 h-32 top-24 right-0 opacity-25"></div>
      <div className="flex justify-between items-end mb-12 relative z-10">
        <div>
          <div className="text-terracotta text-sm tracking-widest uppercase font-bold">{c.eyebrow}</div>
          <h2 className="mt-2 font-serif text-4xl md:text-6xl leading-none">{c.heading} <span className="text-terracotta">{c.heading_accent}</span></h2>
        </div>
        <Link to="/workshops" data-testid="see-all-workshops" className="hidden md:inline-flex items-center gap-1.5 text-terracotta font-bold">{c.link_label} <ArrowRight size={16}/></Link>
      </div>
      <div className="grid md:grid-cols-3 gap-6">
        {items.map((w) => (
          <Link key={w.id} to={`/workshops/${w.id}`} data-testid={`featured-workshop-${w.id}`} className="bg-white rounded-3xl overflow-hidden shadow-card hover:shadow-hover transition-transform hover:-translate-y-1 border-2 border-transparent hover:border-terracotta/30">
            {w.photo_url && <img src={imgUrl(w.photo_url)} alt={w.name} className="h-56 w-full object-cover"/>}
            <div className="p-6">
              <div className="flex items-center gap-2 text-xs flex-wrap">
                <span className="px-3 py-1 rounded-full bg-ochre/15 text-ochre font-semibold">{w.category.replace(/_/g, ' ')}</span>
                <span className="px-3 py-1 rounded-full bg-forest/15 text-forest font-semibold">{w.sold_out || w.spots_remaining === 0 ? "Sold out" : "Available"}</span>
              </div>
              <h3 className="mt-4 font-serif text-2xl leading-tight">{w.name}</h3>
              <p className="mt-1 text-sm text-charcoal/70 font-medium">{w.multiple_dates ? "Multiple dates available" : <>{fmtDateLong(w.date, { weekday: 'short', month: 'short', day: 'numeric' })} · {fmtTime12(w.start_time)}</>}</p>
              <div className="mt-4 flex items-center justify-between">
                <div className="font-serif text-3xl text-terracotta leading-none">${w.effective_price ?? w.price}</div>
                <span className="text-sm text-terracotta font-bold inline-flex items-center gap-1">Book <ArrowRight size={14}/></span>
              </div>
            </div>
          </Link>
        ))}
      </div>
    </section>
  );
}

function Corporate({ c }) {
  return (
    <section className="relative overflow-hidden bg-terracotta text-white py-24">
      <div className="splat splat-cyan w-48 h-48 top-10 left-10 opacity-30"></div>
      <div className="splat splat-purple w-56 h-56 bottom-6 right-6 opacity-30"></div>
      <div className="max-w-7xl mx-auto px-6 lg:px-8 grid md:grid-cols-2 gap-12 items-center relative z-10">
        <div>
          <div className="text-white/80 text-sm tracking-widest uppercase font-bold">{c.eyebrow}</div>
          <h2 className="mt-2 font-serif text-4xl md:text-6xl leading-none text-white">{c.heading} <span className="text-forest-light">{c.heading_accent}</span></h2>
          <p className="mt-6 text-white/90 max-w-lg font-medium">{c.description}</p>
          {c.button_label && <Link to={c.button_link} data-testid="corp-cta-btn" className="mt-8 inline-flex items-center gap-2 rounded-full bg-charcoal text-cream px-8 py-4 font-bold transition-transform hover:-translate-y-1">{c.button_label} <ArrowRight size={18}/></Link>}
        </div>
        {c.image && <img src={imgUrl(c.image)} alt="" className="rounded-3xl w-full h-96 object-cover border-4 border-white shadow-hover"/>}
      </div>
    </section>
  );
}

function Testimonials({ c, items }) {
  if (!items.length) return null;
  return (
    <section className="max-w-7xl mx-auto px-6 lg:px-8 py-24 relative">
      <div className="splat splat-pink w-32 h-32 top-10 left-0 opacity-25"></div>
      <div className="splat splat-cyan w-28 h-28 bottom-10 right-10 opacity-25"></div>
      <div className="text-center max-w-3xl mx-auto relative z-10">
        <a href={c.reviews_link} target="_blank" rel="noreferrer" data-testid="google-reviews-badge" className="inline-flex items-center gap-3 rounded-full bg-white px-5 py-2.5 shadow-card border border-charcoal/10 hover:shadow-hover transition-shadow">
          <svg width="18" height="18" viewBox="0 0 48 48" aria-hidden="true"><path fill="#EA4335" d="M24 9.5c3.5 0 6.6 1.2 9 3.5l6.7-6.7C35.6 2.4 30.1 0 24 0 14.6 0 6.5 5.4 2.6 13.2l7.9 6.1C12.4 13.5 17.7 9.5 24 9.5z"/><path fill="#4285F4" d="M46.5 24.5c0-1.6-.1-3.1-.4-4.5H24v9h12.7c-.5 2.9-2.2 5.3-4.6 6.9l7.5 5.8c4.4-4.1 6.9-10.1 6.9-17.2z"/><path fill="#FBBC05" d="M10.5 28.7c-.5-1.4-.8-2.9-.8-4.7s.3-3.3.8-4.7l-7.9-6.1C1 16.5 0 20.1 0 24s1 7.5 2.6 10.8l7.9-6.1z"/><path fill="#34A853" d="M24 48c6.1 0 11.3-2 15-5.4l-7.5-5.8c-2.1 1.4-4.8 2.2-7.5 2.2-6.3 0-11.6-4-13.5-9.7l-7.9 6.1C6.5 42.6 14.6 48 24 48z"/></svg>
          <span className="font-bold text-charcoal">{c.rating}</span>
          <span className="text-terracotta text-lg leading-none">★★★★★</span>
          <span className="text-charcoal/70 text-sm">{c.reviews_label}</span>
        </a>
        <h2 className="mt-6 font-serif text-4xl md:text-6xl leading-none">{c.heading} <span className="text-terracotta">{c.heading_accent1}</span> <span className="text-forest">{c.heading_accent2}</span></h2>
      </div>
      <div className="mt-16 grid md:grid-cols-3 gap-6 relative z-10">
        {items.slice(0, 3).map((t, i) => (
          <div key={t.id} className={`rounded-3xl p-8 shadow-card border-2 border-charcoal/5 ${i === 1 ? "bg-charcoal text-cream" : "bg-white"}`} data-testid={`testimonial-${t.id}`}>
            <div className={`text-2xl ${i === 1 ? "text-ochre-light" : "text-terracotta"}`}>{"★".repeat(t.rating || 5)}</div>
            <p className={`mt-4 leading-relaxed font-medium text-lg ${i === 1 ? "text-cream/95" : "text-charcoal/85"}`}>"{t.text}"</p>
            <div className="mt-6 flex items-center justify-between">
              <div className={`font-serif text-lg ${i === 1 ? "text-forest-light" : "text-charcoal"}`}>— {t.name}</div>
              {t.source && <div className={`text-xs font-semibold ${i === 1 ? "text-cream/60" : "text-charcoal/60"}`}>via {t.source}</div>}
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}
